import React, { useRef } from 'react';
import { Check, PencilLine, Settings } from 'lucide-react';
import './DefeitosVerveNativeServiceModal.css';
import {
  CAMPOS_GRUPO_KEYS,
  COND_TIPO_OS_FIELDS,
  normCondOsFlag,
  rowTemConfigDefeito,
  rowDefeitoHoverTooltip,
  getSemPendenciaTipoOsIssues,
  formatSemPendenciaTipoOsBlockMessage,
} from './condicoesIntervShared';
import { Ci2ReservaIndicador } from './Ci2ReservaCheck';
import {
  Ci2GrupoEditPopover,
  Ci2SingleFieldEditPopover,
  Ci2DefeitoEditPopover,
} from './CondicoesIntervEditPopovers';
import { Ci2TipoOsInlinePicker } from './Ci2TipoOsInlinePicker';
import { GoHoverTooltip } from '../../go-config/components/GoHoverTooltip';
import { PERM_CFG_TIP_PORTAL_CLASS } from '../../users-customers/components/PermCfgTooltip';

function Ci2TipoOsCfgTip({ row, missing }) {
  const on = COND_TIPO_OS_FIELDS.filter((f) => normCondOsFlag(row[f.db]) === 1);
  return (
    <span className="perm-cfg-tip">
      <span className="perm-cfg-tip-title">Tipos de OS</span>
      <span className="perm-cfg-tip-sec">
        <span className="perm-cfg-tip-sec-label">
          Configurados ({on.length}/{COND_TIPO_OS_FIELDS.length})
        </span>
        {on.length ? (
          <span className="perm-cfg-tip-chips">
            {on.map((f) => (
              <span key={f.db} className="perm-cfg-tip-chip">{f.label}</span>
            ))}
          </span>
        ) : (
          <span className="perm-cfg-tip-empty">Nenhum tipo configurado</span>
        )}
      </span>
      {missing ? (
        <span className="ci2-tipo-os-tip-warn">Condição ativa sem tipo de OS definido.</span>
      ) : null}
      <span className="ci2-tipo-os-tip-hint">Clique para configurar</span>
    </span>
  );
}

function Ci2Switch({ checked, disabled, onChange, tooltip, className = '' }) {
  const switchBtn = (
    <button
      type="button"
      className={`ci2-switch${checked ? ' on' : ''}${className ? ` ${className}` : ''}`}
      disabled={disabled}
      aria-pressed={checked}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled && onChange) {
          onChange({ target: { checked: !checked } });
        }
      }}
    />
  );

  if (!tooltip) return switchBtn;

  return (
    <span className="ci2-switch-tooltip-wrap ci2-modern-tooltip-host" data-tooltip={tooltip}>
      {switchBtn}
    </span>
  );
}

function CampoIntervCell({ row, campoDef, api, onStartEdit }) {
  const {
    CAMPOS_GRUPO_KEYS,
    condicaoEditing,
    condicaoSuccessAnim,
    rowGrupoCamposPreenchidos,
    rowPodeMostrarReservaCheck,
  } = api;

  const { key } = campoDef;
  const isGrupoKey = CAMPOS_GRUPO_KEYS.includes(key);
  const isEditingThisField =
    condicaoEditing?.id === row.TB01055_CODIGO && condicaoEditing?.field === key;
  const isSuccess =
    condicaoSuccessAnim?.id === row.TB01055_CODIGO &&
    (key === 'status_retorno' || key === 'status_peca_disp'
      ? condicaoSuccessAnim?.field === key
      : isGrupoKey && !condicaoSuccessAnim?.field);
  const canEdit = row.func_close === 1;
  const val = row[key] ?? '';
  const trimmed = String(val ?? '').trim();
  const grupoComplete = rowGrupoCamposPreenchidos(row);
  const pecaDispLocked = key === 'status_peca_disp' && canEdit && !grupoComplete;
  const canClickEdit =
    canEdit &&
    !pecaDispLocked &&
    (isGrupoKey ||
      key === 'status_retorno' ||
      (key === 'status_peca_disp' && grupoComplete));

  const handleCellClick = (e) => {
    if (!canClickEdit) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.blur();
    }
    onStartEdit(e, key);
  };

  const renderValue = () => {
    if (key === 'status_retorno') {
      if (!trimmed) return <span className="ci2-muted">—</span>;
      return <span className="ci2-pill ci2-pill-retorno">{trimmed}</span>;
    }
    if (key === 'status_peca_disp') {
      if (!trimmed) return <span className="ci2-muted">—</span>;
      return <span className="ci2-pill ci2-pill-peca">{trimmed}</span>;
    }
    if (['operacao', 'condicao'].includes(key)) {
      return trimmed ? <span className="ci2-mono">{trimmed}</span> : <span className="ci2-muted">—</span>;
    }
    if (key === 'status_disponivel' || key === 'status_indisponivel') {
      return trimmed ? <span className="ci2-mono">{trimmed}</span> : <span className="ci2-muted">—</span>;
    }
    return trimmed ? <span>{trimmed}</span> : <span className="ci2-muted">—</span>;
  };

  const isGrupoCampo = isGrupoKey;
  const editTooltip =
    canClickEdit &&
    (isGrupoCampo || key === 'status_retorno' || key === 'status_peca_disp')
      ? 'Clique para configurar'
      : undefined;

  return (
    <td
      key={key}
      title={
        pecaDispLocked
          ? 'Preencha Operação, Condição, Status disponível e Status indisponível para editar esta coluna.'
          : undefined
      }
      className={[
        'ci2-td',
        'ci2-td-campo',
        `ci2-td-${key}`,
        isGrupoCampo ? 'ci2-td-grupo' : '',
        pecaDispLocked ? 'ci2-td-peca-locked' : '',
        isEditingThisField && !['status_retorno', 'status_peca_disp'].includes(key) ? 'ci2-td-active' : '',
        canClickEdit ? 'ci2-td-editable' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="ci2-cell-wrap">
        {key === 'status_disponivel' || key === 'status_indisponivel' ? (
          <span className="ci2-status-with-reserva">
            {canClickEdit ? (
              <button
                type="button"
                tabIndex={-1}
                className={`ci2-cell-trigger${editTooltip ? ' ci2-modern-tooltip-host' : ''}`}
                data-tooltip={editTooltip}
                onMouseDown={handleCellClick}
              >
                {renderValue()}
              </button>
            ) : (
              renderValue()
            )}
            {rowPodeMostrarReservaCheck(row) ? (
              <Ci2ReservaIndicador row={row} fieldKey={key} />
            ) : null}
          </span>
        ) : canClickEdit ? (
          <button
            type="button"
            tabIndex={-1}
            className={`ci2-cell-trigger${editTooltip ? ' ci2-modern-tooltip-host' : ''}`}
            data-tooltip={editTooltip}
            onMouseDown={handleCellClick}
          >
            {renderValue()}
          </button>
        ) : (
          renderValue()
        )}
        {isSuccess ? (
          <span className="condicoes-success-check" aria-hidden>
            <Check size={10} />
          </span>
        ) : null}
      </div>
    </td>
  );
}

function PermiteFecharCell({ row, api }) {
  const {
    desabilitarOpenId,
    setDesabilitarOpenId,
    desabilitarCondicao,
    disablingCondicaoId,
    disableCondicaoFeedback,
    enablingCondicaoId,
    enableCondicaoFeedback,
    habilitarCondicao,
  } = api;

  return (
    <td className="ci2-td ci2-td-center ci2-td-permite">
      {row.func_close === 1 ? (
        <div className="ci2-permite-wrap">
          {desabilitarOpenId === row.TB01055_CODIGO ? (
            <button
              type="button"
              className="boolean-badge no condicoes-disable-badge"
              onClick={() => desabilitarCondicao(row.TB01055_CODIGO)}
              disabled={disablingCondicaoId === row.TB01055_CODIGO}
              title="Desabilitar condição para fechar OS"
            >
              <span className="boolean-badge-dot" aria-hidden />
              <span className="boolean-badge-text">
                {disablingCondicaoId === row.TB01055_CODIGO ? 'Aguarde...' : 'Desabilitar'}
              </span>
            </button>
          ) : null}
          <button
            type="button"
            className="ci2-lock-toggle on"
            onClick={() =>
              setDesabilitarOpenId((prev) => (prev === row.TB01055_CODIGO ? null : row.TB01055_CODIGO))
            }
            disabled={!!disablingCondicaoId}
            title="Condição habilitada para fechar OS"
            aria-pressed="true"
          >
            <span className="ci2-knob">
              <Check size={7} strokeWidth={3} aria-hidden />
            </span>
          </button>
          {disableCondicaoFeedback &&
          disableCondicaoFeedback.id === row.TB01055_CODIGO &&
          disableCondicaoFeedback.type === 'error' ? (
            <div className="condicoes-msg-erro">
              {disableCondicaoFeedback.msg || 'Erro ao desabilitar'}
            </div>
          ) : null}
        </div>
      ) : enablingCondicaoId === row.TB01055_CODIGO ? (
        <span className="ci2-enabling">
          <span className="ci2-enabling-spinner" aria-hidden />
          Processando...
        </span>
      ) : enableCondicaoFeedback && enableCondicaoFeedback.id === row.TB01055_CODIGO ? (
        enableCondicaoFeedback.type === 'success' ? (
          <span className="condicoes-sim">Sim</span>
        ) : (
          <span className="condicoes-msg-erro">{enableCondicaoFeedback.msg || 'Erro'}</span>
        )
      ) : (
        <button
          type="button"
          className="ci2-enable-affordance ci2-modern-tooltip-host"
          data-tooltip="Clique para habilitar condição"
          onClick={() => habilitarCondicao(row.TB01055_CODIGO, row.TB01055_NOME)}
          aria-label="Habilitar condição"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden>
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>
      )}
    </td>
  );
}

function DefeitoCell({ row, api, onStartEdit }) {
  const { rowHasStatusRetorno, defeitoColInteractive, nativeMode } = api;
  const canEdit = row.func_close === 1 && rowHasStatusRetorno(row) && defeitoColInteractive;
  const configured = rowTemConfigDefeito(row);
  const term = nativeMode ? 'serviço' : 'defeito';
  const termTitle = nativeMode ? 'Serviços' : 'Defeito';

  const gearControl = configured ? (
    <>
      <span className="ci2-defeito-status-check" aria-hidden>
        <Check size={10} strokeWidth={2.5} />
      </span>
      <Settings size={16} strokeWidth={1.75} className="ci2-defeito-icon" aria-hidden />
    </>
  ) : (
    <>
      <span className="ci2-defeito-idle-dash ci2-muted" aria-hidden>
        —
      </span>
      <Settings size={16} strokeWidth={1.75} className="ci2-defeito-icon ci2-defeito-icon--on-hover" aria-hidden />
    </>
  );

  const defeitoTooltip = configured
    ? rowDefeitoHoverTooltip(row, nativeMode)
    : `Clique para configurar ${term}`;

  return (
    <td
      className={`ci2-td ci2-td-defeito${
        row.func_close === 1 && rowHasStatusRetorno(row) ? '' : ' ci2-td-defeito-muted'
      }${
        row.func_close === 1 && rowHasStatusRetorno(row) && !defeitoColInteractive
          ? ' ci2-td-defeito-disabled'
          : ''
      }${canEdit ? ' ci2-td-editable' : ''}`}
      title={
        row.func_close === 1 && rowHasStatusRetorno(row) && !defeitoColInteractive
          ? `${termTitle} em retorno técnico está desabilitado nas Configurações.`
          : row.func_close === 1 && !rowHasStatusRetorno(row)
            ? `Preencha Status de retorno para vincular ${term}.`
            : canEdit
              ? configured
                ? `${nativeMode ? 'Serviço' : 'Defeito'} configurado — clique para editar`
                : `Clique para configurar ${term}`
              : undefined
      }
    >
      <div className="ci2-cell-wrap ci2-defeito-cell-inner">
        {row.func_close === 1 && rowHasStatusRetorno(row) ? (
          canEdit ? (
            <button
              type="button"
              className={`ci2-cell-trigger ci2-defeito-trigger ci2-modern-tooltip-host${
                configured ? ' ci2-defeito-trigger--configured' : ''
              }`}
              data-tooltip={defeitoTooltip}
              onClick={onStartEdit}
              aria-label={configured ? `Editar ${term} configurado` : `Configurar ${term}`}
            >
              <span
                className={`ci2-defeito-gear-wrap${
                  configured ? ' ci2-defeito-gear-wrap--configured' : ''
                }`}
              >
                {gearControl}
              </span>
            </button>
          ) : configured ? (
            <span
              className="ci2-defeito-gear-wrap ci2-defeito-gear-wrap--configured ci2-modern-tooltip-host"
              data-tooltip={rowDefeitoHoverTooltip(row, nativeMode)}
            >
              {gearControl}
            </span>
          ) : (
            <span className="ci2-muted">—</span>
          )
        ) : (
          <span className="ci2-muted">—</span>
        )}
      </div>
    </td>
  );
}

export function CondicoesIntervTableRowV2({ row, cat, api, hidden }) {
  const {
    CAMPOS_INTERV_PREFIX,
    CAMPOS_INTERV_SUFFIX,
    isIntervColVis,
    rowPodeUsarAtualizaSla,
    rowUpdateSlaLigado,
    setCondicoesList,
    API_BASE_URL,
    defeitoExpandId,
    openDefeitoPainel,
    closeDefeitoPainel,
    condicaoEditing,
    startEditCondicaoCampo,
    cancelEditCondicaoCampo,
    disablingCondicaoId,
    tipoOsOpenCod,
    setTipoOsOpenCod,
  } = api;

  const editAnchorRef = useRef(null);
  const defeitoAnchorRef = useRef(null);
  const tipoOsAnchorRef = useRef(null);

  const isGrupoEditOpen = condicaoEditing?.id === row.TB01055_CODIGO && !condicaoEditing?.field;
  const isRetornoEditOpen =
    condicaoEditing?.id === row.TB01055_CODIGO && condicaoEditing?.field === 'status_retorno';
  const isPecaEditOpen =
    condicaoEditing?.id === row.TB01055_CODIGO && condicaoEditing?.field === 'status_peca_disp';
  const isDefeitoOpen = defeitoExpandId === row.TB01055_CODIGO;
  const tipoOsOpen = tipoOsOpenCod != null && String(tipoOsOpenCod) === String(row.TB01055_CODIGO);

  const handleStartCampoEdit = (e, field) => {
    if (defeitoExpandId) closeDefeitoPainel();
    if (setTipoOsOpenCod) setTipoOsOpenCod(null);
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    editAnchorRef.current = e.currentTarget;
    startEditCondicaoCampo(row, field);
    requestAnimationFrame(() => window.scrollTo(scrollX, scrollY));
  };

  const handleStartDefeitoEdit = (e) => {
    e.stopPropagation();
    if (condicaoEditing) cancelEditCondicaoCampo();
    if (setTipoOsOpenCod) setTipoOsOpenCod(null);
    defeitoAnchorRef.current = e.currentTarget;
    if (isDefeitoOpen) {
      closeDefeitoPainel();
    } else {
      openDefeitoPainel(row);
    }
  };

  const handleTipoOsOpen = (e) => {
    if (row.func_close !== 1) return;
    e.preventDefault();
    e.stopPropagation();
    if (condicaoEditing) cancelEditCondicaoCampo();
    if (defeitoExpandId) closeDefeitoPainel();
    tipoOsAnchorRef.current = e.currentTarget;
    const cod = String(row.TB01055_CODIGO);
    setTipoOsOpenCod((prev) => (prev != null && String(prev) === cod ? null : cod));
  };

  const nTipoOn = COND_TIPO_OS_FIELDS.reduce((acc, f) => acc + normCondOsFlag(row[f.db]), 0);
  const tipoOsLabel =
    nTipoOn === COND_TIPO_OS_FIELDS.length ? 'Todos' : `${nTipoOn}/${COND_TIPO_OS_FIELDS.length}`;
  const showTipoOsAlert = !!api.hasGo && cat !== 'desabilitada' && row.func_close === 1 && nTipoOn === 0;

  const rowClass = [
    'ci2-data-row',
    `cat-${cat}`,
    disablingCondicaoId === row.TB01055_CODIGO ? 'ci2-row-disabling' : '',
    isGrupoEditOpen || isRetornoEditOpen || isPecaEditOpen || isDefeitoOpen || tipoOsOpen ? 'ci2-row-editing' : '',
    hidden ? 'ci2-hidden-row' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const patchToggle = async (endpoint, bodyKey, checked, stateKey, savingKey, opts = {}) => {
    const codigo = row.TB01055_CODIGO;
    const exclusive = !!opts.exclusive;
    const otherChecker = opts.hasOther;
    const otherMsg = opts.otherMessage;

    if (checked && typeof otherChecker === 'function' && otherChecker(codigo)) {
      if (api.toastError) api.toastError(otherMsg || 'Já existe outra condição com esta marcação.');
      return;
    }

    setCondicoesList((prev) =>
      prev.map((r) => {
        if (r.TB01055_CODIGO === codigo) {
          return { ...r, [savingKey]: true, [stateKey]: checked ? 1 : 0 };
        }
        if (exclusive && checked) return { ...r, [stateKey]: 0 };
        return r;
      })
    );
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/config/condicoes-intervencao/${endpoint}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_condition: codigo, [bodyKey]: checked }),
      });
      const result = await res.json().catch(() => ({}));
      if (!result.success) {
        setCondicoesList((prev) =>
          prev.map((r) =>
            r.TB01055_CODIGO === codigo
              ? { ...r, [stateKey]: checked ? 0 : 1, [savingKey]: false }
              : r
          )
        );
        if (api.toastError) api.toastError(result.message || 'Erro ao atualizar');
      } else {
        setCondicoesList((prev) =>
          prev.map((r) => {
            if (r.TB01055_CODIGO === codigo) {
              return { ...r, [stateKey]: checked ? 1 : 0, [savingKey]: false };
            }
            if (exclusive && checked) return { ...r, [stateKey]: 0, [savingKey]: false };
            return { ...r, [savingKey]: false };
          })
        );
      }
    } catch {
      setCondicoesList((prev) =>
        prev.map((r) =>
          r.TB01055_CODIGO === codigo
            ? { ...r, [stateKey]: checked ? 0 : 1, [savingKey]: false }
            : r
        )
      );
      if (api.toastError) api.toastError('Erro de conexão');
    }
  };

  return (
    <React.Fragment>
      <tr className={rowClass}>
        <PermiteFecharCell row={row} api={api} />

        <td className="ci2-td ci2-cond-cell">
          <GoHoverTooltip
            text={row.TB01055_NOME || ''}
            className="go-hover-tip-wrap--contents"
            portalClassName="ci2-name-tip-portal"
            align="start"
            onlyWhenTruncated
            delayMs={120}
            zIndex={12000}
          >
            <span className="ci2-cond-name">{String(row.TB01055_NOME || '').slice(0, 60)}</span>
          </GoHoverTooltip>
          {row.is_active === false ? <span className="dv2-inactive-tag">Inativa</span> : null}
          <button
            type="button"
            className="dv2-name-edit-btn"
            onClick={() => api.openEditCondition(row.TB01055_CODIGO)}
            aria-label="Editar condição"
            title="Editar condição"
          >
            <PencilLine size={13} strokeWidth={1.75} aria-hidden />
          </button>
        </td>

        {isIntervColVis('tipo_os') ? (
          <td className={`ci2-td ci2-td-center${row.func_close === 1 ? ' ci2-td-editable' : ''}`}>
            {row.func_close === 1 ? (
              <GoHoverTooltip
                content={<Ci2TipoOsCfgTip row={row} missing={showTipoOsAlert} />}
                portalClassName={PERM_CFG_TIP_PORTAL_CLASS}
                className="go-hover-tip-wrap--contents"
                delayMs={120}
                zIndex={12000}
              >
                <button
                  type="button"
                  tabIndex={-1}
                  ref={tipoOsAnchorRef}
                  className={`ci2-cell-trigger ci2-tipo-os-trigger${tipoOsOpen ? ' ci2-cell-trigger--open' : ''}`}
                  onMouseDown={handleTipoOsOpen}
                >
                  <span className="ci2-tipo-os-tooltip-anchor">
                    <span className="ci2-mono">{tipoOsLabel}</span>
                    {showTipoOsAlert ? <span className="ci2-tipo-os-alert-dot" aria-label="Nenhum tipo de OS configurado" /> : null}
                  </span>
                </button>
              </GoHoverTooltip>
            ) : (
              <span className="ci2-muted">—</span>
            )}
          </td>
        ) : null}

        {isIntervColVis('codigo') ? (
          <td className="ci2-td ci2-mono" title={String(row.TB01055_CODIGO)}>
            {String(row.TB01055_CODIGO).slice(0, 4)}
          </td>
        ) : null}

        {CAMPOS_INTERV_PREFIX.filter((c) => isIntervColVis(c.key)).map((campoDef) => (
          <CampoIntervCell key={campoDef.key} row={row} campoDef={campoDef} api={api} onStartEdit={handleStartCampoEdit} />
        ))}

        {isIntervColVis('defeito') ? (
          <DefeitoCell row={row} api={api} onStartEdit={handleStartDefeitoEdit} />
        ) : null}

        {CAMPOS_INTERV_SUFFIX.filter((c) => isIntervColVis(c.key)).map((campoDef) => (
          <CampoIntervCell key={campoDef.key} row={row} campoDef={campoDef} api={api} onStartEdit={handleStartCampoEdit} />
        ))}

        {isIntervColVis('atualiza_sla') ? (
          <td
            className={`ci2-td ci2-td-center${
              row.func_close === 1 && !rowPodeUsarAtualizaSla(row) ? ' ci2-td-peca-locked' : ''
            }`}
            title={
              row.func_close === 1 && !rowPodeUsarAtualizaSla(row)
                ? 'Preencha Operação, Condição, status dispon./indispon., Status de retorno e Status peça Disp. para editar Atualiza SLA.'
                : undefined
            }
          >
            {row.func_close === 1 ? (
              <Ci2Switch
                checked={rowUpdateSlaLigado(row)}
                disabled={!rowPodeUsarAtualizaSla(row)}
                onChange={async (e) => {
                  const checked = e.target.checked;
                  const codigo = row.TB01055_CODIGO;
                  setCondicoesList((prev) =>
                    prev.map((r) =>
                      r.TB01055_CODIGO === codigo ? { ...r, update_sla: checked ? 1 : 0 } : r
                    )
                  );
                  try {
                    const token = localStorage.getItem('authToken');
                    const res = await fetch(`${API_BASE_URL}/api/config/condicoes-intervencao/update-sla`, {
                      method: 'PATCH',
                      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ id_condition: codigo, updateSla: checked }),
                    });
                    const result = await res.json().catch(() => ({}));
                    if (!result.success) {
                      setCondicoesList((prev) =>
                        prev.map((r) =>
                          r.TB01055_CODIGO === codigo ? { ...r, update_sla: checked ? 0 : 1 } : r
                        )
                      );
                    } else {
                      setCondicoesList((prev) =>
                        prev.map((r) =>
                          r.TB01055_CODIGO === codigo ? { ...r, update_sla: checked ? 1 : 0 } : r
                        )
                      );
                    }
                  } catch {
                    setCondicoesList((prev) =>
                      prev.map((r) =>
                        r.TB01055_CODIGO === codigo ? { ...r, update_sla: checked ? 0 : 1 } : r
                      )
                    );
                  }
                }}
              />
            ) : null}
          </td>
        ) : null}

        {isIntervColVis('func_install') ? (
          <td className="ci2-td ci2-td-center">
            {row.func_close === 1 ? (
              <Ci2Switch
                checked={row.func_install === 1 || row.func_install === true}
                disabled={!!row.__installSaving}
                onChange={(e) =>
                  patchToggle('install', 'install', e.target.checked, 'func_install', '__installSaving', {
                    exclusive: true,
                    hasOther: api.hasOtherInstalaEquip,
                    otherMessage:
                      'Já existe uma condição com "Instala equip." habilitado. Desative-a antes de habilitar em outra.',
                  })
                }
              />
            ) : null}
          </td>
        ) : null}

        {isIntervColVis('func_uninstall') ? (
          <td className="ci2-td ci2-td-center">
            {row.func_close === 1 ? (
              <Ci2Switch
                checked={row.func_uninstall === 1 || row.func_uninstall === true}
                disabled={!!row.__uninstallSaving}
                onChange={(e) =>
                  patchToggle(
                    'uninstall',
                    'uninstall',
                    e.target.checked,
                    'func_uninstall',
                    '__uninstallSaving',
                    {
                      exclusive: true,
                      hasOther: api.hasOtherDesinstalaEquip,
                      otherMessage:
                        'Já existe uma condição com "Desinstala equip." habilitado. Desative-a antes de habilitar em outra.',
                    }
                  )
                }
              />
            ) : null}
          </td>
        ) : null}

        {isIntervColVis('func_counters') ? (
          <td className="ci2-td ci2-td-center">
            {row.func_close === 1 ? (
              <Ci2Switch
                checked={row.func_counters === 1}
                className="ci2-counters-toggle"
                tooltip={
                  row.func_counters === 1
                    ? 'Termine de configurar em Notificações por email, marcando opção de contadores e definindo o email destinatário.'
                    : undefined
                }
                onChange={(e) =>
                  patchToggle('counters', 'notify', e.target.checked, 'func_counters', '__countersSaving')
                }
              />
            ) : null}
          </td>
        ) : null}

        {isIntervColVis('func_nao_liga') ? (
          <td className="ci2-td ci2-td-center">
            {row.func_close === 1 ? (
              <Ci2Switch
                checked={row.func_nao_liga === 1 || row.func_nao_liga === true}
                disabled={
                  !!row.__naoLigaSaving ||
                  ((row.func_nao_liga !== 1 && row.func_nao_liga !== true) &&
                    api.hasOtherNaoLiga?.(row.TB01055_CODIGO))
                }
                tooltip={
                  (row.func_nao_liga !== 1 && row.func_nao_liga !== true) &&
                  api.hasOtherNaoLiga?.(row.TB01055_CODIGO)
                    ? 'Já existe uma condição com "Não liga" habilitado. Desative-a antes de habilitar em outra.'
                    : undefined
                }
                onChange={async (e) => {
                  const checked = e.target.checked;
                  const codigo = row.TB01055_CODIGO;
                  if (checked && api.hasOtherNaoLiga?.(codigo)) return;
                  setCondicoesList((prev) =>
                    prev.map((r) => {
                      if (r.TB01055_CODIGO === codigo) {
                        return { ...r, __naoLigaSaving: true, func_nao_liga: checked ? 1 : 0 };
                      }
                      if (checked) return { ...r, func_nao_liga: 0 };
                      return r;
                    })
                  );
                  try {
                    const token = localStorage.getItem('authToken');
                    const res = await fetch(`${API_BASE_URL}/api/config/condicoes-intervencao/nao-liga`, {
                      method: 'PATCH',
                      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ id_condition: codigo, naoLiga: checked }),
                    });
                    const result = await res.json().catch(() => ({}));
                    if (!result.success) {
                      setCondicoesList((prev) =>
                        prev.map((r) =>
                          r.TB01055_CODIGO === codigo
                            ? { ...r, func_nao_liga: checked ? 0 : 1, __naoLigaSaving: false }
                            : r
                        )
                      );
                      if (api.toastError) api.toastError(result.message || 'Erro ao atualizar Não liga');
                    } else {
                      setCondicoesList((prev) =>
                        prev.map((r) => {
                          if (r.TB01055_CODIGO === codigo) {
                            return { ...r, func_nao_liga: checked ? 1 : 0, __naoLigaSaving: false };
                          }
                          if (checked) return { ...r, func_nao_liga: 0, __naoLigaSaving: false };
                          return { ...r, __naoLigaSaving: false };
                        })
                      );
                    }
                  } catch {
                    setCondicoesList((prev) =>
                      prev.map((r) =>
                        r.TB01055_CODIGO === codigo
                          ? { ...r, func_nao_liga: checked ? 0 : 1, __naoLigaSaving: false }
                          : r
                      )
                    );
                    if (api.toastError) api.toastError('Erro de conexão');
                  }
                }}
              />
            ) : null}
          </td>
        ) : null}

        {isIntervColVis('func_sem_pendencia') ? (
          <td className="ci2-td ci2-td-center">
            {row.func_close === 1 ? (
              <Ci2Switch
                checked={row.func_sem_pendencia === 1 || row.func_sem_pendencia === true}
                disabled={!!row.__semPendenciaSaving}
                onChange={async (e) => {
                  const checked = e.target.checked;
                  const codigo = row.TB01055_CODIGO;
                  if (checked && api.erpTransition && /^N\d+$/i.test(String(codigo || ''))) {
                    if (api.toastError) {
                      api.toastError(
                        'Enquanto houver OS do ERP em aberto nesta transição, "Sem pendência" só pode ficar numa condição com código do ERP. Esta condição existe só no Postgres.'
                      );
                    }
                    return;
                  }
                  if (checked && api.hasOtherSemPendencia?.(codigo)) {
                    if (api.toastError) {
                      api.toastError(
                        'Já existe uma condição com "Sem pendência" habilitado. Desative-a antes de habilitar em outra.'
                      );
                    }
                    return;
                  }
                  if (checked) {
                    const blockMsg = formatSemPendenciaTipoOsBlockMessage(
                      getSemPendenciaTipoOsIssues(row),
                      { forEnable: true }
                    );
                    if (blockMsg) {
                      if (api.toastError) api.toastError(blockMsg);
                      return;
                    }
                  }
                  setCondicoesList((prev) =>
                    prev.map((r) => {
                      if (r.TB01055_CODIGO === codigo) {
                        return { ...r, __semPendenciaSaving: true, func_sem_pendencia: checked ? 1 : 0 };
                      }
                      if (checked) return { ...r, func_sem_pendencia: 0 };
                      return r;
                    })
                  );
                  try {
                    const token = localStorage.getItem('authToken');
                    const res = await fetch(`${API_BASE_URL}/api/config/condicoes-intervencao/sem-pendencia`, {
                      method: 'PATCH',
                      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ id_condition: codigo, semPendencia: checked }),
                    });
                    const result = await res.json().catch(() => ({}));
                    if (!result.success) {
                      setCondicoesList((prev) =>
                        prev.map((r) =>
                          r.TB01055_CODIGO === codigo
                            ? { ...r, func_sem_pendencia: checked ? 0 : 1, __semPendenciaSaving: false }
                            : r
                        )
                      );
                      if (api.toastError) api.toastError(result.message || 'Erro ao atualizar Sem pendência');
                    } else {
                      setCondicoesList((prev) =>
                        prev.map((r) => {
                          if (r.TB01055_CODIGO === codigo) {
                            return { ...r, func_sem_pendencia: checked ? 1 : 0, __semPendenciaSaving: false };
                          }
                          if (checked) return { ...r, func_sem_pendencia: 0, __semPendenciaSaving: false };
                          return { ...r, __semPendenciaSaving: false };
                        })
                      );
                    }
                  } catch {
                    setCondicoesList((prev) =>
                      prev.map((r) =>
                        r.TB01055_CODIGO === codigo
                          ? { ...r, func_sem_pendencia: checked ? 0 : 1, __semPendenciaSaving: false }
                          : r
                      )
                    );
                    if (api.toastError) api.toastError('Erro de conexão');
                  }
                }}
              />
            ) : null}
          </td>
        ) : null}
      </tr>

      {!hidden && tipoOsOpen ? (
        <Ci2TipoOsInlinePicker
          open
          anchorRef={tipoOsAnchorRef}
          row={row}
          api={api}
          onClose={() => setTipoOsOpenCod(null)}
        />
      ) : null}

      {!hidden && isGrupoEditOpen ? (
        <Ci2GrupoEditPopover open anchorRef={editAnchorRef} row={row} api={api} />
      ) : null}
      {!hidden && isRetornoEditOpen ? (
        <Ci2SingleFieldEditPopover
          open
          anchorRef={editAnchorRef}
          row={row}
          fieldKey="status_retorno"
          label="Status de retorno"
          maxLen={2}
          api={api}
        />
      ) : null}
      {!hidden && isPecaEditOpen ? (
        <Ci2SingleFieldEditPopover
          open
          anchorRef={editAnchorRef}
          row={row}
          fieldKey="status_peca_disp"
          label="Status peça Disp."
          maxLen={2}
          api={api}
        />
      ) : null}
      {!hidden && isDefeitoOpen ? (
        <Ci2DefeitoEditPopover open anchorRef={defeitoAnchorRef} row={row} api={api} />
      ) : null}
    </React.Fragment>
  );
}
